/**
 * The site-wide contact form. A thin wrapper over the schema-driven
 * <LeadForm> (src/forms/) so every page that embeds <ContactForm /> shares one
 * submission path to Zoho.
 *
 * Context comes from props and URL params (?product=&category=&source=; URL
 * wins because it carries click-through intent). Pages can pass richer
 * `context` (industryLabel, locationLabel, platformLabel, caseStudy) and a
 * different `schema` when the form should be identified separately in the CRM.
 */
import { useSearchParams } from "react-router-dom";
import LeadForm from "@/forms/engine/LeadForm";
import contactGeneral from "@/forms/definitions/contactGeneral";
import productEnquiry from "@/forms/definitions/productEnquiry";

export default function ContactForm({ product, category, source, context, schema, consentVariant } = {}) {
  const [searchParams] = useSearchParams();
  const ctx = {
    ...(context || {}),
    product: searchParams.get("product") || product || "",
    category: searchParams.get("category") || category || "",
    entry_point: searchParams.get("source") || source || "",
  };
  const defaults = ctx.product
    ? { message: `Hi, I'm interested in the ${ctx.product}. Please can you provide more information?` }
    : undefined;
  const header = ctx.product ? (
    <div
      className="flex items-center gap-2 text-brand-aqua bg-brand-aqua-light border border-brand-aqua/20 rounded-xl px-4 py-2.5 text-sm"
      data-testid="form-product-context"
    >
      {"Enquiring about: "}
      <strong>{ctx.product}</strong>
    </div>
  ) : null;

  // A product in context makes this a product enquiry (its own form id in the
  // CRM, plus volume / machine-count questions) unless the page chose a schema.
  const resolved = schema || (ctx.product ? productEnquiry : contactGeneral);
  return <LeadForm schema={resolved} context={ctx} defaults={defaults} header={header} consentVariant={consentVariant} />;
}
